// First-party experiment-arm transport (OMG-2490, Refund Guarantee Test;
// OMG-2684, Organic Funnel Test).
//
// The Cloudflare edge worker / cms middleware assign an arm and write it to a
// first-party cookie at .omgyes.com (contract frozen in marketing-tracking's
// OMG-2500 ledger entry). This module reads those cookies at checkout-submit
// time and shapes them into the body fields omg-api persists to
// experiment_assignment — the row the test's revenue read is computed from.
//
// SEND-GATED behind VITE_SEND_EXPERIMENT_ASSIGNMENTS, exactly like
// VITE_TOS_VERSION: the prod backend rejects unknown fields (tsoa
// throw-on-extras — see the 6/25 idempotencyKey outage), so these fields must
// not be sent to a stage until the omg-api that accepts them is live there:
// OMG-2489 / mono#462 for `experiments` + `experimentVisitorId`, OMG-2555 /
// mono#487 for `experimentAssigner` (both on prod since the 10 Sep train).
// Set the var to "true" per stage, after that deploy, in ALL the root
// .env.<stage> files (they win over vite/.env).
// NB: Vite inlines env at BUILD time — flipping it needs a redeploy.

import { readCookie } from "./fbCapiContext";

/**
 * Experiment key → the cookie carrying its arm. The KEY here must be
 * byte-identical to omg-api's registry (models/experiments.ts) and to the
 * OMG-2500 ledger entry, or the backend silently drops the entry; the cookie
 * value is `<experiment_key>:<arm>` (URL-encoded on write; readCookie
 * decodes). A new experiment = one row here + a registry row over there.
 */
export const ARM_COOKIES: Readonly<Record<string, string>> = {
  // v1 (Sep to Oct 2026) and v2 (OMG-2682) share the cookie; the value's
  // `<key>:` prefix says which test minted it, and readArmCookie only
  // yields an arm for the key it is asked about, so a browser holding a v1
  // cookie relays v1 and nothing else. v1 stays so the last v1 purchases
  // still land on their rows during the switch-over; omg-api keeps both keys.
  refund_guarantee_2026: "omgyes_arm_rg26",
  refund_guarantee_2026_v2: "omgyes_arm_rg26",
  // OMG-2684, the organic funnel test (3-step "C" funnel vs the current
  // 2-step funnel), assigned by the website-cms middleware on English www
  // /join. Its own cookie, independent of the refund one, so a visitor in
  // both tests relays both arms on the same purchase.
  organic_funnel_2026: "omgyes_arm_of26",
};

/** The bucketed visitor id, minted by the edge worker on first touch. Links
 *  this purchase to the visitor-grain exposure rows (OMG-2499's
 *  exposure-vs-purchase reconciliation). */
export const VISITOR_ID_COOKIE = "omgyes_vid_rg26";

/**
 * Which assigner minted the arm (OMG-2555): `edge` = the Cloudflare splash
 * assigner (paid funnel), `cms` = the www pricing-page middleware (organic).
 * Written beside the arm cookie by whichever assigner bucketed the visitor,
 * relayed here as `experimentAssigner`, persisted by omg-api as
 * `experiment_assignment.metadata.assigner`. It is the ONLY record of the
 * paid/organic split in the revenue read. omg-api allowlists the value
 * (models/experiments.ts KNOWN_ASSIGNERS) and drops anything else without
 * dropping the arm, so this list must stay byte-identical to that one.
 */
export const ASSIGNER_COOKIE = "omgyes_src_rg26";
export const KNOWN_ASSIGNERS = ["edge", "cms"] as const;
export type ExperimentAssigner = (typeof KNOWN_ASSIGNERS)[number];

export interface ExperimentAssignmentFields {
  experiments: Record<string, string>;
  experimentVisitorId?: string;
  experimentAssigner?: ExperimentAssigner;
}

/** The allowlisted assigner a cookie value names, or undefined for anything
 *  else (absent, unknown word, wrong case). Same rule as omg-api's
 *  resolveAssigner, applied here so an unknown value is never sent: the
 *  backend would drop it anyway, but sending only what it accepts keeps the
 *  wire honest and keeps a future rename from producing a silent 100%-dropped
 *  field. */
export const resolveAssigner = (
  raw: string | undefined,
): ExperimentAssigner | undefined => {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  return (KNOWN_ASSIGNERS as readonly string[]).includes(trimmed)
    ? (trimmed as ExperimentAssigner)
    : undefined;
};

/** The send gate shared by every consumer of these cookies: the checkout body
 *  and the Segment event stamp (OMG-2619) switch on and off together, so a
 *  stage never reports arms in Mixpanel that its revenue rows do not carry. */
export const experimentAssignmentsEnabled = (): boolean =>
  import.meta.env.VITE_SEND_EXPERIMENT_ASSIGNMENTS?.trim() === "true";

/**
 * The arm an arm cookie carries for `experimentKey`, or undefined when the
 * cookie is absent or malformed. Missing separator, empty arm, or a prefix
 * naming a DIFFERENT experiment than this cookie should carry: per the
 * OMG-2464 contract that reads as nothing stored, not as an error. The single
 * parser for these cookies; both the checkout body and the event stamp use it.
 */
export const readArmCookie = (
  experimentKey: string,
  cookieName: string,
): string | undefined => {
  const raw = readCookie(cookieName);
  if (!raw) return undefined;
  const separator = raw.indexOf(":");
  if (separator <= 0) return undefined;
  const cookieKey = raw.slice(0, separator);
  const arm = raw.slice(separator + 1);
  if (cookieKey !== experimentKey || !arm) return undefined;
  return arm;
};

/**
 * The checkout-body fields for every arm cookie present and well-formed, or
 * undefined when there is nothing to send (gate off, no cookies, or nothing
 * parseable — a malformed cookie is treated as absent, never as an error:
 * this path can never be allowed to complicate a purchase). The visitor id
 * only rides along with at least one arm — alone it identifies nothing.
 */
export const experimentAssignments = ():
  | ExperimentAssignmentFields
  | undefined => {
  if (!experimentAssignmentsEnabled()) return undefined;

  const experiments: Record<string, string> = {};
  for (const [experimentKey, cookieName] of Object.entries(ARM_COOKIES)) {
    const arm = readArmCookie(experimentKey, cookieName);
    if (arm) experiments[experimentKey] = arm;
  }

  if (Object.keys(experiments).length === 0) return undefined;

  const experimentVisitorId = readCookie(VISITOR_ID_COOKIE);
  // Provenance rides only with an arm, like the visitor id: alone it
  // attributes nothing. Absent key, not undefined-valued, for the same
  // throw-on-extras reason as every optional field in this body.
  const experimentAssigner = resolveAssigner(readCookie(ASSIGNER_COOKIE));
  return {
    experiments,
    ...(experimentVisitorId && { experimentVisitorId }),
    ...(experimentAssigner && { experimentAssigner }),
  };
};

// ---------------------------------------------------------------------------
// OMG-2619: the Refund Guarantee Test arm on analytics events.
//
// The checkout body above feeds the revenue read; these properties let
// Mixpanel break the checkout FUNNEL (begin_checkout, field events,
// client_side_purchase, ...) down by arm. Contract, identical in leaflet,
// omgyes-website-cms and here:
//   rg26_arm       `control` | `guarantee`, from omgyes_arm_rg26
//   rg26_assigner  `edge` | `cms`, from omgyes_src_rg26
// Each key is omitted (never null or empty) when its cookie is absent or
// invalid, and the two are independent of each other.
//
// OMG-2684 adds the Organic Funnel Test arm beside them, under a contract now
// shared with leaflet (`app/lp/[slug]/organic-funnel.ts`) and
// omgyes-website-cms (`lib/experiments/analyticsProps.ts`):
//   of26_arm       `control` | `three_step`, from omgyes_arm_of26
// No assigner key: the funnel arm has one assigner, the www middleware. Like
// the rg26 keys it reports the arm the cookie HOLDS, not whether the split is
// live, so a Mixpanel read must be windowed to the dates the split ran.

// The ONE key the Segment stamp reports under rg26_arm: v2 (OMG-2682). A v1
// cookie stamps neither rg26_arm nor rg26_assigner once this ships (the
// assigner is only reported beside a known arm), which is what a stopped
// test looks like in Mixpanel; the relay above still carries the v1 arm to
// the purchase row.
const RG26_EXPERIMENT_KEY = "refund_guarantee_2026_v2";
export const RG26_EVENT_ARMS = ["control", "guarantee"] as const;
export type Rg26EventArm = (typeof RG26_EVENT_ARMS)[number];

export interface Rg26EventProperties {
  rg26_arm?: Rg26EventArm;
  rg26_assigner?: ExperimentAssigner;
}

/**
 * The rg26 event properties the current cookies support, read fresh on every
 * call (the assigner may write the cookie after this bundle loaded). Returns
 * an empty object when the send gate is off or nothing valid is stored, so
 * spreading it is always safe and adds no keys for visitors without an arm.
 */
export const rg26EventProperties = (): Rg26EventProperties => {
  if (!experimentAssignmentsEnabled()) return {};
  const arm = readArmCookie(
    RG26_EXPERIMENT_KEY,
    ARM_COOKIES[RG26_EXPERIMENT_KEY],
  );
  const knownArm = (
    RG26_EVENT_ARMS as readonly (string | undefined)[]
  ).includes(arm)
    ? (arm as Rg26EventArm)
    : undefined;
  // The assigner is only meaningful beside an arm of THIS test: a browser
  // holding only a v1 arm (or junk) still carries omgyes_src_rg26, and
  // stamping it alone would put a visitor "in" the test with no arm (Jason,
  // react#1052 review).
  const assigner = knownArm
    ? resolveAssigner(readCookie(ASSIGNER_COOKIE))
    : undefined;
  return {
    ...(knownArm && { rg26_arm: knownArm }),
    ...(assigner && { rg26_assigner: assigner }),
  };
};

// OMG-2684: the organic funnel arm. Same key and cookie as the checkout body
// relay above (ARM_COOKIES), parsed by the same readArmCookie.
const OF26_EXPERIMENT_KEY = "organic_funnel_2026";
export const OF26_EVENT_ARMS = ["control", "three_step"] as const;
export type Of26EventArm = (typeof OF26_EVENT_ARMS)[number];

export interface Of26EventProperties {
  of26_arm?: Of26EventArm;
}

/**
 * The of26 event property the current cookies support, read fresh on every
 * call. Empty when the send gate is off, the cookie is absent, or it holds
 * another experiment's key or an unknown arm.
 */
export const of26EventProperties = (): Of26EventProperties => {
  if (!experimentAssignmentsEnabled()) return {};
  const arm = readArmCookie(
    OF26_EXPERIMENT_KEY,
    ARM_COOKIES[OF26_EXPERIMENT_KEY],
  );
  return (OF26_EVENT_ARMS as readonly (string | undefined)[]).includes(arm)
    ? { of26_arm: arm as Of26EventArm }
    : {};
};

/** Every cookie-derived event property, both experiments. */
export type ExperimentEventProperties = Rg26EventProperties &
  Of26EventProperties;

/** Minimal shape of the event a Segment source middleware receives. */
interface StampableEvent {
  type?: string;
  properties?: Record<string, unknown>;
}

/**
 * Writes both experiments' properties onto a `page` or `track` event, LAST,
 * so a caller property of the same name cannot redefine an arm. Any other
 * event type (identify, group, alias) is left untouched: an arm is an event
 * property, never a user trait. The keys are RESERVED: a caller's value is
 * dropped even when this visitor holds no arm, so "no key" always means "no
 * arm" in Mixpanel (same rule as leaflet and omgyes-website-cms). No-op when
 * there is nothing to stamp or strip, so an event from a visitor without an
 * arm cookie ships byte-identical.
 */
const RESERVED_EXPERIMENT_KEYS = [
  "rg26_arm",
  "rg26_assigner",
  "of26_arm",
] as const;

export const stampExperimentProperties = (event: StampableEvent): void => {
  if (event.type !== "page" && event.type !== "track") return;
  const props: ExperimentEventProperties = {
    ...rg26EventProperties(),
    ...of26EventProperties(),
  };
  const current = event.properties;
  const callerSetReserved =
    current !== undefined &&
    RESERVED_EXPERIMENT_KEYS.some((key) =>
      Object.prototype.hasOwnProperty.call(current, key),
    );
  if (Object.keys(props).length === 0 && !callerSetReserved) return;
  const next: Record<string, unknown> = { ...current };
  for (const key of RESERVED_EXPERIMENT_KEYS) delete next[key];
  event.properties = { ...next, ...props };
};
